import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import TextField from "@components/ui/textfields/TextField";
import SearchField from "@components/ui/textfields/SearchField";
import { Text1, Text2, Text3 } from "@components/ui/texts/Texts";
import { useDataStore } from "@store/user_org";
import { useProjectStore } from "@store/projectStore";
import { useState, useEffect, useCallback, Fragment } from "react";
import { showNotification } from "context/notification.service";
import {
  billingTemplatesApi,
  BillingTemplate,
  BillingStep,
  BillingMappings,
} from "@services/api/billingTemplates";
import { Template, ProjectTheme } from "types/project";
import { organizationsApi } from "@services/api/organizations";
import CheckBoxInput from "@components/ui/selection/CheckBoxInput";
import { isOwnerOrAdmin } from "@utils/permissions";
import HelpTooltip from "@components/ui/HelpTooltip";
import {
  MemberAssignmentItem,
  orgMembersToUsers,
} from "@components/ui/blocks/MemberAssignmentBlock";

// ---------------------------------------------------------------------------
// Modal editor (like ProjectTemplateModal)
// ---------------------------------------------------------------------------

function BillingTemplateModal({
  onClose,
  template,
  projectTemplates,
  onSave,
  onDelete,
}: {
  onClose: () => void;
  template: BillingTemplate | null;
  projectTemplates: Template[];
  onSave: (t: BillingTemplate) => Promise<void>;
  onDelete?: () => void;
}) {
  const isNew = !template;
  const [name, setName] = useState(template?.name || "");
  const [steps, setSteps] = useState<BillingStep[]>(
    template?.steps?.length
      ? template.steps
      : [{ label: "Démarrage", percentage: 10, is_demarrage: true }],
  );
  const [mappings, setMappings] = useState<BillingMappings>(
    template?.mappings || {},
  );
  const [isSaving, setIsSaving] = useState(false);

  // Mapping UI state
  const [selectedProjectId, setSelectedProjectId] = useState<string>("");

  const totalPercentage = steps.reduce((sum, s) => sum + (s.percentage || 0), 0);

  // Current project template for mapping
  const selectedProject = projectTemplates.find((p) => p.id === selectedProjectId);
  const projectThemes: ProjectTheme[] = selectedProject?.themes || [];

  // Get current mapping for selected project
  const currentMapping = selectedProjectId ? (mappings[selectedProjectId] || []) : [];

  // Which project templates already have mappings
  const mappedProjectIds = Object.keys(mappings).filter(
    (k) => mappings[k].length > 0,
  );

  // --- Step CRUD ---

  const addStep = () => {
    setSteps([...steps, { label: "", percentage: 0, is_demarrage: false }]);
  };

  const removeStep = (index: number) => {
    setSteps(steps.filter((_, i) => i !== index));
    const newMappings: BillingMappings = {};
    for (const [ptId, maps] of Object.entries(mappings)) {
      newMappings[ptId] = maps
        .filter((m) => m.step_index !== index)
        .map((m) => ({
          ...m,
          step_index: m.step_index > index ? m.step_index - 1 : m.step_index,
        }));
    }
    setMappings(newMappings);
  };

  const updateStep = (index: number, field: keyof BillingStep, value: any) => {
    setSteps(
      steps.map((s, i) =>
        i === index
          ? { ...s, [field]: field === "percentage" ? parseFloat(value) || 0 : value }
          : s,
      ),
    );
  };

  const toggleDemarrage = (index: number) => {
    setSteps(
      steps.map((s, i) =>
        i === index ? { ...s, is_demarrage: !s.is_demarrage } : s,
      ),
    );
  };

  // --- Mapping ---

  const toggleBillingStepLink = (billingIndex: number, projectStepId: string) => {
    if (!selectedProjectId) return;

    setMappings((prev) => {
      let existing = [...(prev[selectedProjectId] || [])].map((m) => ({ ...m, linked_steps: [...m.linked_steps] }));

      const entry = existing.find((m) => m.step_index === billingIndex);
      const isCurrentlyLinked = entry?.linked_steps.includes(projectStepId);

      if (isCurrentlyLinked) {
        existing = existing.map((m) =>
          m.step_index === billingIndex
            ? { ...m, linked_steps: m.linked_steps.filter((s) => s !== projectStepId) }
            : m,
        ).filter((m) => m.linked_steps.length > 0);
      } else {
        // Each billing step can only have one project step
        if (entry) {
          entry.linked_steps = [projectStepId];
        } else {
          existing.push({ step_index: billingIndex, linked_steps: [projectStepId] });
        }
      }

      return { ...prev, [selectedProjectId]: existing };
    });
  };

  const removeProjectMapping = (ptId: string) => {
    setMappings((prev) => {
      const next = { ...prev };
      delete next[ptId];
      return next;
    });
    if (selectedProjectId === ptId) {
      setSelectedProjectId("");
    }
  };

  // --- Save ---

  const handleSave = async () => {
    if (!name.trim()) {
      showNotification({ type: "error", message: "Le nom est obligatoire" });
      return;
    }
    if (steps.length === 0) {
      showNotification({ type: "error", message: "Ajoutez au moins une étape" });
      return;
    }
    if (steps.some((s) => !s.label.trim())) {
      showNotification({ type: "error", message: "Toutes les étapes doivent avoir un libellé" });
      return;
    }
    setIsSaving(true);
    try {
      await onSave({
        ...(template || { id: "", org_id: "", created_at: "" }),
        name,
        steps,
        mappings,
      } as BillingTemplate);
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  const getProjectName = (ptId: string) => {
    const pt = projectTemplates.find((t) => t.id === ptId);
    return pt?.infos?.general?.name || ptId;
  };

  // Flatten themes → steps (sous-thèmes) → controlpoints for the matrix rows
  const flatRows: { cpId: string; cpLabel: string; themeName: string; stepName: string }[] = [];
  for (const theme of projectThemes) {
    for (const step of theme.steps) {
      for (const cp of step.controlpoints) {
        flatRows.push({
          cpId: cp.id,
          cpLabel: cp.label,
          themeName: theme.label,
          stepName: step.label,
        });
      }
    }
  }

  // Group flat rows for display: theme > step headers
  type RowGroup = {
    themeName: string;
    steps: {
      stepName: string;
      controlpoints: { cpId: string; cpLabel: string }[];
    }[];
  };
  const rowGroups: RowGroup[] = [];
  for (const theme of projectThemes) {
    const stepsGroup: RowGroup["steps"] = [];
    for (const step of theme.steps) {
      if (step.controlpoints.length > 0) {
        stepsGroup.push({
          stepName: step.label,
          controlpoints: step.controlpoints.map((cp) => ({ cpId: cp.id, cpLabel: cp.label })),
        });
      }
    }
    if (stepsGroup.length > 0) {
      rowGroups.push({ themeName: theme.label, steps: stepsGroup });
    }
  }

  const allBillingSteps = steps.map((s, i) => ({ ...s, originalIndex: i }));

  // Group demarrage steps into a single column for the mapping matrix
  const demarrageIndices = allBillingSteps.filter((s) => s.is_demarrage).map((s) => s.originalIndex);
  const demarragePercentage = allBillingSteps.filter((s) => s.is_demarrage).reduce((sum, s) => sum + s.percentage, 0);
  const avancementStepsForMapping = allBillingSteps.filter((s) => !s.is_demarrage);
  // Columns for the mapping table: one "Démarrage" + each avancement step
  const mappingColumns: { label: string; percentage: number; indices: number[]; isDemarrage: boolean }[] = [];
  if (demarrageIndices.length > 0) {
    mappingColumns.push({ label: "Démarrage", percentage: demarragePercentage, indices: demarrageIndices, isDemarrage: true });
  }
  for (const s of avancementStepsForMapping) {
    mappingColumns.push({ label: s.label, percentage: s.percentage, indices: [s.originalIndex], isDemarrage: false });
  }

  // Toggle mapping for a column (may affect multiple step indices for demarrage)
  const toggleColumnLink = (columnIndices: number[], cpId: string) => {
    if (!selectedProjectId) return;
    setMappings((prev) => {
      let existing = [...(prev[selectedProjectId] || [])].map((m) => ({ ...m, linked_steps: [...m.linked_steps] }));

      // Check if ANY of the column's indices is already linked to this cp
      const isLinked = columnIndices.some((idx) => {
        const entry = existing.find((m) => m.step_index === idx);
        return entry?.linked_steps.includes(cpId);
      });

      if (isLinked) {
        // Unlink from all column indices
        existing = existing.map((m) =>
          columnIndices.includes(m.step_index)
            ? { ...m, linked_steps: m.linked_steps.filter((s) => s !== cpId) }
            : m,
        ).filter((m) => m.linked_steps.length > 0);
      } else {
        // Link to all column indices
        for (const idx of columnIndices) {
          const entry = existing.find((m) => m.step_index === idx);
          if (entry) {
            entry.linked_steps = [cpId];
          } else {
            existing.push({ step_index: idx, linked_steps: [cpId] });
          }
        }
      }

      return { ...prev, [selectedProjectId]: existing };
    });
  };

  return (
    <BaseModal
      title={isNew ? "Nouveau modèle de facturation" : "Modifier le modèle"}
      startIcon={<Icon1 name="invoice" size={20} />}
      onClose={onClose}
      width="max-w-4xl"
    >
      <div className="bg-white flex flex-col gap-4 p-4">
        <TextField
          label="Nom du modèle"
          value={name}
          onChange={(v) => setName(v)}
          placeholder="Facturation maison individuelle"
        />

        {/* ---- STEPS DEFINITION ---- */}
        <div className="flex flex-col gap-1">
          <div className="text-h3 font-semibold text-neutral-600">Étapes de facturation</div>

          <div className="flex flex-row items-center gap-2 px-2 py-1 text-h4 text-neutral-400">
            <div className="w-8 text-center">#</div>
            <div className="flex-1">Libellé</div>
            <div className="w-24">Abrév.</div>
            <div className="w-80">%</div>
            <div className="w-24 text-center">Type</div>
            <div className="w-8" />
          </div>

          {steps.map((step, index) => (
            <div key={index} className="flex flex-row items-center gap-2 px-2 py-1 rounded-lg hover:bg-neutral-50">
              <div className="w-8 text-center text-h4 text-neutral-400">{index + 1}</div>
              <div className="flex-1">
                <input
                  type="text"
                  className="w-full border rounded-lg px-2 py-1.5 text-h3 outline-none focus:ring-2 focus:ring-neutral-300"
                  value={step.label}
                  onChange={(e) => updateStep(index, "label", e.target.value)}
                  placeholder="Nom de l'étape..."
                />
              </div>
              <div className="w-24">
                <input
                  type="text"
                  className="w-full border rounded-lg px-2 py-1.5 text-h3 outline-none focus:ring-2 focus:ring-neutral-300"
                  value={step.abbreviation || ""}
                  onChange={(e) => updateStep(index, "abbreviation", e.target.value)}
                  placeholder="Abrév."
                />
              </div>
              <div className="w-80">
                <input
                  type="number"
                  className="w-full border rounded-lg px-2 py-1.5 text-h3 text-right outline-none focus:ring-2 focus:ring-neutral-300"
                  value={step.percentage || ""}
                  onChange={(e) => updateStep(index, "percentage", e.target.value)}
                  placeholder="0"
                  min={0}
                  max={100}
                />
              </div>
              <div className="w-24 flex justify-center items-center gap-1">
                <button
                  className={`px-2 py-1 rounded-lg text-h4 border ${
                    step.is_demarrage
                      ? "bg-green-50 text-green-600 border-green-200"
                      : "bg-white text-neutral-400 border-neutral-200"
                  }`}
                  onClick={() => toggleDemarrage(index)}
                >
                  {step.is_demarrage ? "Démarrage" : "Avancement"}
                </button>
                <HelpTooltip content="Une étape « Démarrage » correspond au premier appel de fonds (acompte ou lancement). Elle est traitée différemment des étapes d'avancement dans les factures et les rapports comptables." />
              </div>
              {!step.is_demarrage && (
                <div className="flex items-center gap-1">
                  {/* Payment delay */}
                  <div className="flex items-center gap-0.5" title="Délai de paiement (jours)">
                    <span className="text-h5">⏰</span>
                    <input
                      type="number"
                      className="w-10 border rounded px-1 py-1 text-h5 text-center outline-none focus:ring-2 focus:ring-neutral-300"
                      value={step.payment_delay_days ?? 15}
                      onChange={(e) => updateStep(index, "payment_delay_days", parseInt(e.target.value) || 15)}
                      min={1}
                      max={365}
                    />
                    <HelpTooltip content="Délai accordé au client pour régler la facture après son émission. Utilisé pour calculer la date d'échéance affichée sur la facture et dans les relances." size={12} />
                  </div>
                  {/* Project delay */}
                  <div className="flex items-center gap-0.5">
                  <button
                    className={`px-2 py-1 rounded-lg text-h5 border ${
                      step.project_delay_days
                        ? "bg-red-50 text-red-500 border-red-200"
                        : "bg-white text-neutral-300 border-neutral-200"
                    }`}
                    onClick={() => updateStep(index, "project_delay_days", step.project_delay_days ? null : 30)}
                    title="Alerte si facture non créée avant la fin du projet"
                  >
                    ⚠️
                  </button>
                  <HelpTooltip content="Déclenche une alerte si cette étape de facturation n'a toujours pas été émise X jours avant la date de fin prévue du projet. Aide à éviter d'oublier de facturer en fin de chantier." size={12} />
                  </div>
                  {step.project_delay_days && (
                    <input
                      type="number"
                      className="w-10 border rounded px-1 py-1 text-h5 text-center outline-none focus:ring-2 focus:ring-neutral-300"
                      value={step.project_delay_days}
                      onChange={(e) => updateStep(index, "project_delay_days", parseInt(e.target.value) || 30)}
                      min={1}
                      max={365}
                      title="Jours avant fin du projet"
                    />
                  )}
                  {/* Step gap delay */}
                  <div className="flex items-center gap-0.5">
                  <button
                    className={`px-2 py-1 rounded-lg text-h5 border ${
                      step.step_gap_delay_days
                        ? "bg-blue-50 text-blue-500 border-blue-200"
                        : "bg-white text-neutral-300 border-neutral-200"
                    }`}
                    onClick={() => updateStep(index, "step_gap_delay_days", step.step_gap_delay_days ? null : 30)}
                    title="Alerte si non envoyée X jours après l'étape précédente"
                  >
                    ↔
                  </button>
                  <HelpTooltip content="Déclenche une alerte si cette étape n'a pas été envoyée X jours après la précédente. Utile pour maintenir un rythme de facturation régulier et éviter les trous de trésorerie." size={12} />
                  </div>
                  {step.step_gap_delay_days && (
                    <input
                      type="number"
                      className="w-10 border rounded px-1 py-1 text-h5 text-center outline-none focus:ring-2 focus:ring-neutral-300"
                      value={step.step_gap_delay_days}
                      onChange={(e) => updateStep(index, "step_gap_delay_days", parseInt(e.target.value) || 30)}
                      min={1}
                      max={365}
                      title="Jours après l'étape précédente"
                    />
                  )}
                </div>
              )}
              <button
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-neutral-300 hover:text-red-500"
                onClick={() => removeStep(index)}
              >
                <Icon1 name="effacer2" size={16} />
              </button>
            </div>
          ))}

          <button
            className="flex flex-row items-center gap-2 px-2 py-2 text-h4 text-primary hover:bg-blue-50 rounded-lg w-fit"
            onClick={addStep}
          >
            <Icon1 name="plus" size={14} />
            Ajouter une étape
          </button>

          {/* Total */}
          <div className="flex flex-row items-center gap-2 px-2 py-2 border-t">
            <div className="w-8" />
            <div className="flex-1 text-h3 font-semibold">Total</div>
            <div className={`w-80 text-right text-h3 font-semibold ${Math.abs(totalPercentage - 100) < 0.01 ? "text-green-600" : "text-red-500"}`}>
              {totalPercentage.toFixed(1)} %
            </div>
            <div className="w-24" />
            <div className="w-8" />
          </div>
          {Math.abs(totalPercentage - 100) >= 0.01 && (
            <div className="text-h4 text-red-500">
              Le total doit faire 100 % (actuellement {totalPercentage.toFixed(1)} %)
            </div>
          )}
        </div>

        {/* ---- MAPPINGS ---- */}
        <div className="flex flex-col gap-3 border-t pt-4">
          <div className="text-h3 font-semibold text-neutral-600">
            <div className="flex flex-row items-center gap-1.5">
              <span>Mapping avec les modèles projet</span>
              <HelpTooltip content="Associe chaque étape de facturation à un point de contrôle du projet. Lorsqu'un point de contrôle est validé, l'étape de facturation correspondante devient automatiquement prête à être émise." />
            </div>
          </div>
          <div className="text-h4 text-neutral-400">
            Pour chaque modèle projet, cochez à quelle étape de facturation correspond chaque point de contrôle.
          </div>

          {/* Tabs for project templates */}
          <div className="flex flex-row flex-wrap gap-2">
            {projectTemplates.map((pt) => {
              const ptId = pt.id;
              const isActive = selectedProjectId === ptId;
              const hasMappings = mappedProjectIds.includes(ptId);
              return (
                <button
                  key={ptId}
                  className={`px-3 py-1.5 rounded-lg text-h4 border transition-colors ${
                    isActive
                      ? "bg-primary text-white border-primary"
                      : hasMappings
                        ? "bg-blue-50 text-primary border-blue-200 hover:bg-blue-100"
                        : "bg-white text-neutral-500 border-neutral-200 hover:bg-neutral-50"
                  }`}
                  onClick={() => {
                    if (isActive) {
                      setSelectedProjectId("");
                    } else {
                      if (!mappings[ptId]) {
                        setMappings((prev) => ({ ...prev, [ptId]: [] }));
                      }
                      setSelectedProjectId(ptId);
                    }
                  }}
                >
                  {pt.infos?.general?.name || ptId}
                  {hasMappings && !isActive && <span className="ml-1 text-h5">●</span>}
                </button>
              );
            })}
          </div>

          {/* Matrix table for selected project */}
          {selectedProjectId && selectedProject && (
            <div className="bg-neutral-50 rounded-lg border overflow-hidden">
              <div className="flex flex-row items-center justify-between px-3 py-2 border-b bg-white">
                <div className="text-h3 font-medium text-neutral-600">
                  {getProjectName(selectedProjectId)}
                </div>
                {mappedProjectIds.includes(selectedProjectId) && (
                  <button
                    className="text-h4 text-red-400 hover:text-red-600 flex items-center gap-1"
                    onClick={() => removeProjectMapping(selectedProjectId)}
                  >
                    <Icon1 name="effacer2" size={14} />
                    Retirer le mapping
                  </button>
                )}
              </div>

              {allBillingSteps.length === 0 ? (
                <div className="text-h4 text-neutral-400 px-3 py-4 text-center">
                  Ajoutez des étapes pour configurer le mapping.
                </div>
              ) : rowGroups.length === 0 ? (
                <div className="text-h4 text-neutral-400 px-3 py-4 text-center">
                  Ce modèle projet n'a pas de points de contrôle.
                </div>
              ) : (
                <div className="overflow-auto max-h-[400px] border rounded-lg">
                  <table className="w-full border-collapse">
                    <thead className="sticky top-0 z-30">
                      <tr className="border-b bg-white">
                        <th className="text-left text-h4 font-semibold text-neutral-500 px-3 py-2 min-w-200 bg-neutral-50 sticky left-0 z-40">
                          Point de contrôle
                        </th>
                        {mappingColumns.map((col, ci) => (
                          <th key={ci} className="text-center text-h4 font-semibold px-3 py-2 min-w-100 bg-white text-neutral-500">
                            <div>{col.label}</div>
                            <div className="text-h5 font-normal text-neutral-400">
                              {col.percentage}%
                              {col.isDemarrage && <span className="ml-1 text-green-500">●</span>}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rowGroups.map((group) => (
                        <Fragment key={group.themeName}>
                          {/* Theme header */}
                          <tr>
                            <td
                              colSpan={mappingColumns.length + 1}
                              className="text-h5 font-semibold text-neutral-400 px-3 pt-3 pb-0.5 bg-neutral-50 sticky left-0 z-10"
                            >
                              {group.themeName}
                            </td>
                          </tr>
                          {group.steps.map((stepGroup) => (
                            <Fragment key={stepGroup.stepName}>
                              {/* Step (sous-thème) sub-header */}
                              <tr>
                                <td
                                  colSpan={mappingColumns.length + 1}
                                  className="text-h5 text-neutral-400 px-3 pl-5 pt-1.5 pb-0.5 bg-neutral-50 italic sticky left-0 z-10"
                                >
                                  {stepGroup.stepName}
                                </td>
                              </tr>
                              {/* Controlpoints (actual rows) */}
                              {stepGroup.controlpoints.map((cp) => {
                                // Find ALL billing step indices this cp is linked to
                                const linkedBillingIndices = new Set<number>();
                                for (const m of currentMapping) {
                                  if (m.linked_steps.includes(cp.cpId)) {
                                    linkedBillingIndices.add(m.step_index);
                                  }
                                }

                                return (
                                  <tr key={cp.cpId} className="border-b border-neutral-100 hover:bg-white">
                                    <td className="text-h4 text-neutral-600 px-3 pl-7 py-1.5 bg-neutral-50 sticky left-0 z-10">
                                      {cp.cpLabel}
                                    </td>
                                    {mappingColumns.map((col, ci) => {
                                      const isLinked = col.indices.some((idx) => linkedBillingIndices.has(idx));
                                      // Check if any index in this column is occupied by another cp
                                      const isOccupiedByOther = col.indices.some((idx) => {
                                        const entry = currentMapping.find((m) => m.step_index === idx);
                                        const occupiedBy = entry?.linked_steps[0];
                                        return occupiedBy != null && occupiedBy !== cp.cpId;
                                      });
                                      return (
                                        <td key={ci} className="text-center px-3 py-1.5">
                                          <button
                                            className={`w-4 h-4 rounded border inline-flex items-center justify-center transition-colors ${
                                              isLinked
                                                ? "border-primary bg-primary"
                                                : isOccupiedByOther
                                                  ? "border-neutral-200 bg-neutral-100 cursor-not-allowed"
                                                  : "border-neutral-300 hover:border-primary"
                                            }`}
                                            onClick={() => {
                                              if (!isOccupiedByOther) {
                                                toggleColumnLink(col.indices, cp.cpId);
                                              }
                                            }}
                                            disabled={isOccupiedByOther}
                                            title={isOccupiedByOther ? "Déjà lié à un autre point de contrôle" : undefined}
                                          >
                                            {isLinked && (
                                              <span className="text-white text-h6">✓</span>
                                            )}
                                          </button>
                                        </td>
                                      );
                                    })}
                                  </tr>
                                );
                              })}
                            </Fragment>
                          ))}
                        </Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-row justify-between border-t pt-4">
          <div>
            {!isNew && onDelete && (
              <SecondaryButton label="Supprimer" onClick={onDelete} variant="small" />
            )}
          </div>
          <div className="flex flex-row gap-2">
            <SecondaryButton label="Annuler" onClick={onClose} variant="small" />
            <Button
              label={isSaving ? "Enregistrement..." : "Enregistrer"}
              onClick={handleSave}
              disabled={isSaving}
            />
          </div>
        </div>
      </div>
    </BaseModal>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Documentation panel — explains how billing works end-to-end
// ---------------------------------------------------------------------------

const DocSection = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-neutral-100 first:border-t-0">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex flex-row items-center justify-between py-3 text-left hover:bg-neutral-50 px-1 rounded"
      >
        <div className="text-h3 font-medium text-neutral-700">{title}</div>
        <svg
          className={`w-4 h-4 text-neutral-400 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="pb-3 pl-1 pr-3 text-h4 text-neutral-600 flex flex-col gap-2">
          {children}
        </div>
      )}
    </div>
  );
};

const FacturationDocPanel = () => (
  <div className="flex flex-col mt-8 bg-white max-w-900 p-4 rounded-lg border-1">
    <div className="flex flex-row items-center gap-2 mb-2">
      <Icon1 name="question" size={16} color="#6b7280" />
      <div className="text-h2 font-semibold text-neutral-700">
        Comment ça marche
      </div>
    </div>
    <div className="text-h4 text-neutral-400 mb-2">
      Un aperçu du fonctionnement de la facturation et de son lien avec
      Pennylane.
    </div>

    <DocSection title="Connexion à Pennylane">
      <div>
        L'outil est connecté à votre compte Pennylane grâce à une clé
        d'API. Une fois la connexion établie, toutes les factures
        d'avancement sont créées depuis l'outil qui affiche leur état en temps quasi-réel.
      </div>
      <div>
        Si Pennylane n'est pas connecté, vous pouvez quand même définir vos
        modèles et suivre vos projets, mais aucune facture ne sera créée
        automatiquement.
      </div>
    </DocSection>

    <DocSection title="Les modèles de facturation">
      <div>
        Un modèle découpe le montant total d'un projet en plusieurs étapes,
        chacune associée à un pourcentage — par exemple&nbsp;: Démarrage
        30&nbsp;%, Hors d'eau 30&nbsp;%, Hors d'air 30&nbsp;%, Finitions
        10&nbsp;%. Le total doit toujours faire 100&nbsp;%.
      </div>
      <div>
        Chaque étape peut être reliée à un ou plusieurs points de contrôle
        d'un modèle de projet. Dès qu'un point de contrôle relié est
        validé sur un projet, l'étape de facturation correspondante devient
        «&nbsp;prête à facturer&nbsp;».
      </div>
      <div>
        Un même modèle de facturation peut servir à plusieurs modèles de
        projet.
      </div>
    </DocSection>

    <DocSection title="Cycle de vie d'une facture">
      <div>
        Chaque facture passe par plusieurs étapes avant d'être encaissée.
        Vous les retrouvez sur la page Facturation d'un projet&nbsp;:
      </div>
      <div className="pl-3 flex flex-col gap-1">
        <div>
          • <b>À facturer</b>&nbsp;: l'étape est prête mais aucune facture
          n'a encore été créée.
        </div>
        <div>
          • <b>Brouillon</b>&nbsp;: une facture brouillon existe dans
          Pennylane, vous pouvez encore la modifier.
        </div>
        <div>
          • <b>Finalisée</b>&nbsp;: la facture a son numéro et son PDF, mais
          elle n'a pas encore été envoyée au client.
        </div>
        <div>
          • <b>Envoyée</b>&nbsp;: le client a reçu la facture par mail (et
          les comptables en copie si configuré ci-dessus).
        </div>
        <div>
          • <b>Payée</b>&nbsp;: le paiement a été encaissé. C'est l'état
          final.
        </div>
      </div>
      <div>
        Si vous supprimez une facture directement dans Pennylane, Hexaflow
        le détecte automatiquement et remet l'étape en «&nbsp;À facturer&nbsp;».
      </div>
    </DocSection>

    <DocSection title="Synchronisation avec Pennylane">
      <div>
        L'outil ne reçoit pas de notification quand quelque chose change
        dans Pennylane. À la place, il va <b>vérifier régulièrement</b> ce
        qui a bougé. Cela se fait de trois manières, qui se complètent&nbsp;:
      </div>
      <div className="pl-3 flex flex-col gap-1">
        <div>
          • <b>Automatiquement toutes les 15 minutes</b>&nbsp;: en tâche de
          fond, l'outil vérifie toutes les factures non encore payées de
          votre organisation et met à jour leur état (envoyée, payée, etc.)
          ainsi que les indicateurs de retard.
        </div>
        <div>
          • <b>Quand vous cliquez sur «&nbsp;Synchroniser&nbsp;»</b> sur la
          page Suivi de facturation&nbsp;: utile si vous venez de marquer
          une facture comme payée dans Pennylane et ne voulez pas attendre
          les 15 minutes.
        </div>
        <div>
          • <b>Quand vous ouvrez l'onglet Facturation d'un projet</b>&nbsp;:
          ce projet est alors rafraîchi immédiatement, en arrière-plan.
        </div>
      </div>
      <div>
        Pour économiser les appels à Pennylane, les factures déjà payées ne
        sont plus jamais revérifiées.
      </div>
    </DocSection>

    <DocSection title="Indicateurs de retard">
      <div>
        L'outil surveille en permanence deux types de retard et les
        signale par des pastilles rouges sur les pages Suivi de facturation
        et Suivi des salariés&nbsp;:
      </div>
      <div className="pl-3 flex flex-col gap-1">
        <div>
          • <b>Retard de paiement</b>&nbsp;: une facture a été envoyée au
          client mais n'est toujours pas payée au-delà du délai défini (15
          jours par défaut).
        </div>
        <div>
          • <b>Retard de facturation</b>&nbsp;: la fin du planning d'un
          projet approche, mais une étape n'a toujours pas été facturée.
        </div>
      </div>
      <div>
        Ces retards déclenchent aussi l'envoi automatique des mails
        d'alerte configurés ci-dessus.
      </div>
    </DocSection>

    <DocSection title="Alertes et notifications">
      <div>
        Chaque matin à 6h00, l'outil envoie un mail récapitulatif des
        retards de paiement et de facturation aux conducteurs de travaux
        concernés, ainsi qu'aux membres listés dans «&nbsp;Alertes
        facturation&nbsp;» ci-dessus.
      </div>
      <div>
        À chaque fois qu'une facture est envoyée à un client, les
        comptables cochés dans «&nbsp;Copie des factures&nbsp;» reçoivent
        automatiquement une copie du mail.
      </div>
    </DocSection>

    <DocSection title="Qui voit quoi">
      <div>
        Les <b>Propriétaires</b> et <b>Managers</b> voient la facturation
        de tous les projets et peuvent tout faire (créer, envoyer,
        synchroniser).
      </div>
      <div>
        Les autres membres (conducteurs, commerciaux, etc.) ne voient que
        les projets dont ils sont conducteurs — sauf s'ils sont listés dans
        «&nbsp;Accès complet au suivi de facturation&nbsp;» ci-dessus.
      </div>
      <div>
        Les clients ne voient que les factures de leurs
        propres projets.
      </div>
    </DocSection>
  </div>
);

export const BillingTemplates = () => {
  const userData = useDataStore((state) => state.userData);
  const orgData = useDataStore((state) => state.orgData);
  const orgMembers = useDataStore((state) => state.orgMembers);
  const projectTemplates = useProjectStore((state) => state.projectTemplates);
  const [templates, setTemplates] = useState<BillingTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  // CC config
  const comptables = orgMembers.filter((m) => m.role === "comptability" && m.status !== "inactive");
  const [billingCcIds, setBillingCcIds] = useState<string[]>(orgData?.config?.billing_cc_user_ids || []);
  const [savingCc, setSavingCc] = useState(false);

  // Billing alerts config
  const allUsers = orgMembersToUsers(orgMembers.filter((m) => m.config?.access_type !== "GUEST"));
  const [billingAlertUserIds, setBillingAlertUserIds] = useState<string[]>((orgData?.config as any)?.billing_alert_user_ids || []);
  const [savingAlerts, setSavingAlerts] = useState(false);

  // Full access config
  const [billingFullAccessIds, setBillingFullAccessIds] = useState<string[]>(orgData?.config?.billing_full_access_user_ids || []);
  const [savingFullAccess, setSavingFullAccess] = useState(false);

  const disabled = !isOwnerOrAdmin(userData);

  const getUserInfo = (userId: string) => {
    const user = allUsers.find((u) => u.id === userId);
    return user ? { name: user.name, color: user.color } : { name: "Inconnu", color: undefined };
  };

  useEffect(() => {
    setBillingCcIds(orgData?.config?.billing_cc_user_ids || []);
    setBillingAlertUserIds((orgData?.config as any)?.billing_alert_user_ids || []);
    setBillingFullAccessIds(orgData?.config?.billing_full_access_user_ids || []);
  }, [orgData?.config]);

  const { fetchOrgData } = useDataStore();

  const toggleCcUser = async (userId: string) => {
    if (!orgData?.id) return;
    const next = billingCcIds.includes(userId)
      ? billingCcIds.filter((id) => id !== userId)
      : [...billingCcIds, userId];
    setBillingCcIds(next);
    setSavingCc(true);
    try {
      await organizationsApi.update(orgData.id, { config: { billing_cc_user_ids: next } });
      await fetchOrgData(orgData.id);
    } catch {
      setBillingCcIds(billingCcIds);
      showNotification({ type: "error", message: "Erreur lors de la sauvegarde" });
    } finally {
      setSavingCc(false);
    }
  };

  const saveAlertUserIds = async (next: string[]) => {
    if (!orgData?.id) return;
    setBillingAlertUserIds(next);
    setSavingAlerts(true);
    try {
      await organizationsApi.update(orgData.id, { config: { billing_alert_user_ids: next } });
      await fetchOrgData(orgData.id);
    } catch {
      setBillingAlertUserIds(billingAlertUserIds);
      showNotification({ type: "error", message: "Erreur lors de la sauvegarde" });
    } finally {
      setSavingAlerts(false);
    }
  };

  const saveFullAccessUserIds = async (next: string[]) => {
    if (!orgData?.id) return;
    setBillingFullAccessIds(next);
    setSavingFullAccess(true);
    try {
      await organizationsApi.update(orgData.id, { config: { billing_full_access_user_ids: next } });
      await fetchOrgData(orgData.id);
    } catch {
      setBillingFullAccessIds(billingFullAccessIds);
      showNotification({ type: "error", message: "Erreur lors de la sauvegarde" });
    } finally {
      setSavingFullAccess(false);
    }
  };

  const fetchTemplates = useCallback(async () => {
    if (!userData?.org_id) return;
    setIsLoading(true);
    try {
      const result = await billingTemplatesApi.list(userData.org_id);
      setTemplates(result);
    } catch {
      showNotification({ type: "error", message: "Erreur lors du chargement" });
    } finally {
      setIsLoading(false);
    }
  }, [userData?.org_id]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleCreate = async (t: BillingTemplate) => {
    if (!userData?.org_id) return;
    try {
      const result = await billingTemplatesApi.create({
        name: t.name,
        org_id: userData.org_id,
        steps: t.steps,
        mappings: t.mappings,
      });
      setTemplates((prev) => [...prev, result]);
      setIsCreating(false);
      showNotification({ type: "success", message: "Modèle créé" });
    } catch {
      showNotification({ type: "error", message: "Erreur lors de la création" });
    }
  };

  const handleUpdate = async (t: BillingTemplate) => {
    try {
      const result = await billingTemplatesApi.update(t.id, {
        name: t.name,
        steps: t.steps,
        mappings: t.mappings,
      });
      setTemplates((prev) => prev.map((x) => (x.id === result.id ? result : x)));
      setEditingId(null);
      showNotification({ type: "success", message: "Modèle enregistré" });
    } catch {
      showNotification({ type: "error", message: "Erreur lors de la mise à jour" });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await billingTemplatesApi.delete(id);
      setTemplates((prev) => prev.filter((x) => x.id !== id));
      setEditingId(null);
      showNotification({ type: "success", message: "Modèle supprimé" });
    } catch {
      showNotification({ type: "error", message: "Erreur lors de la suppression" });
    }
  };

  const filtered = templates.filter((t) =>
    t.name.toLowerCase().includes(search.toLowerCase()),
  );

  const editingTemplate = editingId
    ? templates.find((t) => t.id === editingId) || null
    : null;

  const closeModal = () => {
    setIsCreating(false);
    setEditingId(null);
  };

  return (
    <div className="flex flex-col h-fit pb-96 mt-10 mx-4 lg:ml-10">
      <div className="flex flex-col w-full max-w-400 gap-4">
        <div className="flex flex-col">
          <Text1>Facturation</Text1>
          <Text2>Définissez vos échéanciers de facturation par avancement.</Text2>
          <Text3>
            Chaque modèle découpe le montant total en étapes avec un
            pourcentage, et peut être mappé à plusieurs modèles projet.
          </Text3>
        </div>
      </div>

      <div className="flex flex-col gap-2 mt-4 bg-white max-w-900 p-4 rounded-lg border-1">
        <div className="flex flex-row gap-2 items-center">
          <div className="w-full max-w-400">
            <SearchField
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un modèle..."
            />
          </div>
        </div>

        <div className="flex flex-col divide-y mt-4">
          {isLoading && (
            <div className="text-h3 text-neutral-400 py-8 text-center">Chargement...</div>
          )}
          {!isLoading && filtered.length === 0 && (
            <div className="text-h3 text-neutral-400 py-8 text-center">Aucun modèle de facturation</div>
          )}
          {filtered.map((tmpl) => {
            const totalPct = tmpl.steps.reduce((s, st) => s + st.percentage, 0);
            const mappingCount = Object.keys(tmpl.mappings || {}).length;
            return (
              <div key={tmpl.id} className="flex flex-row rounded-xl items-center px-4 py-2">
                <div className="flex flex-col w-250">
                  <div className="text-h2 font-medium">{tmpl.name}</div>
                </div>
                <div className="text-h3 text-neutral-400 mr-auto flex flex-row gap-3">
                  <span>{tmpl.steps.length} étapes</span>
                  <span className={Math.abs(totalPct - 100) < 0.01 ? "text-green-600" : "text-red-500"}>
                    {totalPct.toFixed(0)} %
                  </span>
                  <span>{mappingCount} modèle(s) projet</span>
                </div>
                <SecondaryButton
                  label="Modifier"
                  variant="small"
                  onClick={() => { setIsCreating(false); setEditingId(tmpl.id); }}
                />
              </div>
            );
          })}
        </div>

        <Button
          className="mt-4"
          label="Nouveau modèle de facturation"
          onClick={() => { setEditingId(null); setIsCreating(true); }}
        />
      </div>

      {/* CC Comptables */}
      <div className="flex flex-col gap-2 mt-8 bg-white max-w-900 p-4 rounded-lg border-1">
        <div className="text-h2 font-semibold text-neutral-700">Copie des factures</div>
        <div className="text-h4 text-neutral-400">
          Les comptables cochés seront en copie du mail de chaque facture envoyée au client.
        </div>
        {comptables.length === 0 ? (
          <div className="text-h4 text-neutral-400 py-4 text-center">
            Aucun membre avec le rôle « comptabilité » dans l'équipe.
          </div>
        ) : (
          <div className="flex flex-col divide-y mt-2">
            {comptables.map((member) => (
              <div key={member.id} className="flex flex-row items-center gap-3 px-2 py-2">
                <CheckBoxInput
                  checked={billingCcIds.includes(member.id)}
                  onChange={() => toggleCcUser(member.id)}
                  disabled={savingCc}
                />
                <div className="flex flex-col">
                  <div className="text-h3 font-medium text-neutral-700">
                    {member.infos?.display_name || member.infos?.full_name || member.infos?.email}
                  </div>
                  <div className="text-h4 text-neutral-400">{member.infos?.email}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Billing alerts */}
      <div className="flex flex-col gap-2 mt-8 bg-white max-w-900 p-4 rounded-lg border-1">
        <div className="text-h2 font-semibold text-neutral-700">Alertes facturation</div>
        <div className="text-h4 text-neutral-400">
          Ces membres recevront les alertes de retard de paiement et de facturation en retard sur le planning,
          en plus du conducteur de travaux du projet.
        </div>

        <div className="flex flex-col gap-2 mt-2">
          {billingAlertUserIds.map((uid) => {
            const info = getUserInfo(uid);
            return (
              <div key={uid} className="flex flex-row items-center justify-between px-3 py-2 rounded-lg bg-neutral-50">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: info.color || "#ccc" }} />
                  <span className="text-h3 text-neutral-700">{info.name}</span>
                </div>
                {!disabled && (
                  <button
                    className="text-h4 text-red-400 hover:text-red-600"
                    onClick={() => saveAlertUserIds(billingAlertUserIds.filter((id) => id !== uid))}
                    disabled={savingAlerts}
                  >
                    Retirer
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {!disabled && (
          <MemberAssignmentItem
            label="Ajouter un membre"
            assignMemberToRole={(userId) => {
              if (userId && !billingAlertUserIds.includes(userId)) {
                saveAlertUserIds([...billingAlertUserIds, userId]);
              }
            }}
            users={allUsers.filter((u) => !billingAlertUserIds.includes(u.id))}
            currentUserId={undefined}
            memberName="Ajouter"
            unassigned={true}
          />
        )}
      </div>

      {/* Full access */}
      <div className="flex flex-col gap-2 mt-8 bg-white max-w-900 p-4 rounded-lg border-1">
        <div className="text-h2 font-semibold text-neutral-700">Accès complet au suivi de facturation</div>
        <div className="text-h4 text-neutral-400">
          Ces membres peuvent voir la facturation de tous les projets dans le suivi de facturation.
          Les autres membres ne voient que les projets dont ils sont conducteurs de travaux.
          Les administrateurs ont toujours accès à tout.
        </div>

        <div className="flex flex-col gap-2 mt-2">
          {billingFullAccessIds.map((uid) => {
            const info = getUserInfo(uid);
            return (
              <div key={uid} className="flex flex-row items-center justify-between px-3 py-2 rounded-lg bg-neutral-50">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: info.color || "#ccc" }} />
                  <span className="text-h3 text-neutral-700">{info.name}</span>
                </div>
                {!disabled && (
                  <button
                    className="text-h4 text-red-400 hover:text-red-600"
                    onClick={() => saveFullAccessUserIds(billingFullAccessIds.filter((id) => id !== uid))}
                    disabled={savingFullAccess}
                  >
                    Retirer
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {!disabled && (
          <MemberAssignmentItem
            label="Ajouter un membre"
            assignMemberToRole={(userId) => {
              if (userId && !billingFullAccessIds.includes(userId)) {
                saveFullAccessUserIds([...billingFullAccessIds, userId]);
              }
            }}
            users={allUsers.filter((u) => !billingFullAccessIds.includes(u.id))}
            currentUserId={undefined}
            memberName="Ajouter"
            unassigned={true}
          />
        )}
      </div>

      <FacturationDocPanel />

      {/* Modal */}
      {(isCreating || editingId) && (
        <BillingTemplateModal
          template={isCreating ? null : editingTemplate}
          projectTemplates={projectTemplates}
          onSave={isCreating ? handleCreate : handleUpdate}
          onDelete={editingId ? () => handleDelete(editingId) : undefined}
          onClose={closeModal}
        />
      )}
    </div>
  );
};
